import questionnairesService from "@/services/questionnairesService";

const state = {
    questionnaire: {},
    questionnaires: []
};

const getters = {
    questionnaire: state => state.questionnaire,
    isQuestionnaireHeaderValid(state) {
        const { program, organization, contactPerson, companyManager, dateStart, dateEnd } = state.questionnaire;
        return program != null && organization != null && contactPerson != null && companyManager != null && dateStart != null && dateEnd != null;
    },
    isElementOmitted: state => id => {
        return state.questionnaire.questionnaireElementsOmitteds?.some(element => element.id === id) || false;
    },
    omittedElements: state => {
        return state.questionnaire.questionnaireElementsOmitteds || []
    },
    getQuestionnaireAnswers: state => {
        return state.questionnaire.questionnaireAnswers || [];
    }
};

const actions = {
    async getAllQuestionnaires({state}, filters) {
        try {
            const {data} = await questionnairesService.getAllTable(filters);
            return {
                data: data,
                error: false
            }
        } catch (error) {
            return {
                error: true,
                message: 'Error fetching questionnaires'
            }
        }
    },
    async saveQuestionnaire({ state }, preliminary) {
        const { questionnaire } = state;
        
        // Validations
        if (!questionnaire.program) {
            return {
            error: true,
            message: 'El programa es obligatorio'
            };
        }
        if (!questionnaire.contactPerson) {
            return {
            error: true,
            message: 'La persona de contacto es obligatoria'
            };
        }
        if (!questionnaire.organization) {
            return {
            error: true,
            message: 'La organización es obligatoria'
            };
        }
        if (!questionnaire.companyManager) {
            return {
            error: true,
            message: 'El gerente de la empresa es obligatorio'
            };
        }

        if (preliminary) {
            questionnaire.processStatus = {id: 1};
        } else {
            questionnaire.processStatus = {id: 3};
        }
        questionnaire.prioritizationStatus = {id: 2};
        try {
            const {data} = await questionnairesService.save(questionnaire);
            if (preliminary) {
                state.questionnaire.id = data.id;
            }
            return {
                ...data,
                error: false,
                message: `Se ha ${preliminary ? 'procesado' : 'registrado'} el diagnostico con ID: ${data.id}`
            };
        } catch (error) {
            const backendMessage = error.response?.data?.message
                || error.response?.data?.details?.[0];
            return {
                error: true,
                message: backendMessage || 'Error saving questionnaire'
            };
        }
    },
    async getQuestionnaireById({ commit, state }, id) {
        try {
            const { data } = await questionnairesService.getAllById(id);
            state.questionnaire = data;

            if (data.questionnaireAnswersDTOList) {
                data.questionnaireAnswersDTOList.forEach(answer => {
                    commit('addQuestion', answer);
                });
            }
            if (data.questionnaireElementsOmittedDTOList) {
                commit('fillOmittedElements', data.questionnaireElementsOmittedDTOList);
            }

            return {
                data: data,
                error: false
            };
        } catch (error) {
            return {
                error: true,
                message: 'Error fetching questionnaires'
            };
        }
    },
    async deleteQuestionnaire({ commit }, id) {
        try {
            const { data } = await questionnairesService.deleteQuestionnaire(id);
            return {
                data: data,
                error: false
            };
        } catch (error) {
            return {
                error: true,
                message: 'Error deleting questionnaire'
            };
        }
    },
    // Finds the most recent COMPLETED diagnostic for an organization, to offer it
    // as a base for a new one. Returns { previous: { id, date } } or previous: null.
    async getMostRecentCompletedForOrganization(context, organizationId) {
        try {
            const { data } = await questionnairesService.getAllCompletedTable({ organization: organizationId });
            const list = Array.isArray(data) ? data : (data?.data ?? data?.content ?? []);
            if (!list.length) {
                return { error: false, previous: null };
            }
            // ISO date strings (YYYY-MM-DD) sort chronologically as plain strings.
            const sorted = [...list].sort((a, b) => {
                const da = String(a.dateEnd ?? a.dateStart ?? '');
                const db = String(b.dateEnd ?? b.dateStart ?? '');
                return db.localeCompare(da);
            });
            const previous = sorted[0];
            return {
                error: false,
                previous: { id: previous.id, date: previous.dateEnd ?? previous.dateStart ?? null }
            };
        } catch (error) {
            return { error: true, message: 'Error al buscar diagnósticos anteriores' };
        }
    },
    // Loads a previous diagnostic and injects its answers, omissions, and
    // contact/manager into the CURRENT questionnaire, stripping every row id and
    // the source questionnaire id, so saving creates a new PENDING record.
    async applyPreviousDiagnosticAsBase({ commit }, previousId) {
        try {
            const { data } = await questionnairesService.getAllById(previousId);
            (data.questionnaireAnswersDTOList || []).forEach(answer => {
                commit('addQuestion', {
                    question: { id: answer.question.id },
                    answer: answer.answer ? { id: answer.answer.id } : null,
                    omitted: !!answer.omitted
                });
            });
            const strippedOmitted = (data.questionnaireElementsOmittedDTOList || []).map(omitted => ({
                element: { id: omitted.element.id },
                omitted: !!omitted.omitted,
                observations: omitted.observations ?? null
            }));
            commit('fillOmittedElements', strippedOmitted);
            const contactPersonId = data.contactPerson ? data.contactPerson.id : null;
            const companyManagerId = data.companyManager ? data.companyManager.id : null;
            if (contactPersonId != null) commit('setContactPerson', { id: contactPersonId });
            if (companyManagerId != null) commit('setCompanyManager', { id: companyManagerId });
            return { error: false, contactPersonId, companyManagerId };
        } catch (error) {
            return { error: true, message: 'Error al cargar el diagnóstico anterior' };
        }
    }
};

const mutations = {
    setProgram(state, program) {
        state.questionnaire.program = program;
    },
    setOrganization(state, organization) {
        state.questionnaire.organization = organization;
    },
    setContactPerson(state, contactPerson) {
        state.questionnaire.contactPerson = contactPerson;
    },
    setCompanyManager(state, companyManager) {
        state.questionnaire.companyManager = companyManager;
    },
    setProcessStatus(state, processStatus) {
        state.questionnaire.processStatus = processStatus;
    },
    setDateStart(state, dateStart) {
        state.questionnaire.dateStart = dateStart;
    },
    setDateEnd(state, dateEnd) {
        state.questionnaire.dateEnd = dateEnd;
    },
    addQuestion(state, question) {        
        if (!state.questionnaire.questionnaireAnswers) {
            state.questionnaire.questionnaireAnswers = [];
        }
        const existingQuestionIndex = state.questionnaire.questionnaireAnswers.findIndex(q => q.question.id === question.question.id);
        if (existingQuestionIndex !== -1) {
            if (state.questionnaire.questionnaireAnswers[existingQuestionIndex].hasOwnProperty('id')) {
                question.id = state.questionnaire.questionnaireAnswers[existingQuestionIndex].id;
            }
            state.questionnaire.questionnaireAnswers.splice(existingQuestionIndex, 1, question);
        } else {
            state.questionnaire.questionnaireAnswers.push(question);
        }
    },
    removeQuestion(state, question) {        
        if (!state.questionnaire.questionnaireAnswers) {
            return;
        }
        const existingQuestionIndex = state.questionnaire.questionnaireAnswers.findIndex(q => q.question.id === question.id);
        if (existingQuestionIndex !== -1) {
            state.questionnaire.questionnaireAnswers.splice(existingQuestionIndex, 1);
        }
    },
    addOmittedElements(state, {element}) {
        if (!state.questionnaire.questionnaireElementsOmitteds) {
            state.questionnaire.questionnaireElementsOmitteds = [];
            state.questionnaire.questionnaireElementsOmitteds.push({ element, omitted: true });
            return;
        }        
        const elementIndex = state.questionnaire.questionnaireElementsOmitteds.findIndex(e => e.element.id === element.id);
        if (elementIndex !== -1) {
            state.questionnaire.questionnaireElementsOmitteds[elementIndex].omitted = true;
        } else {
            state.questionnaire.questionnaireElementsOmitteds.push({ element, omitted: true });
        }
    },
    removeOmittedElements(state, omittedElement) {
        if (!state.questionnaire.questionnaireElementsOmitteds) {
            return;
        }
        const elementIndex = state.questionnaire.questionnaireElementsOmitteds.findIndex(e => e.element.id === omittedElement.id);
        if (elementIndex !== -1) {
            if (state.questionnaire.questionnaireElementsOmitteds[elementIndex].observations) {
                state.questionnaire.questionnaireElementsOmitteds[elementIndex].omitted = false;
            } else {
                state.questionnaire.questionnaireElementsOmitteds.splice(elementIndex, 1);
            }            
        }
    },
    addElementsObservation(state, {element, observations}) {
        if (!state.questionnaire.questionnaireElementsOmitteds) {
            state.questionnaire.questionnaireElementsOmitteds = [];
            state.questionnaire.questionnaireElementsOmitteds.push({ element, observations, omitted: false });
            return;
        }
        const elementIndex = state.questionnaire.questionnaireElementsOmitteds.findIndex(e => e.element.id === element.id);
        if (elementIndex !== -1) {
            state.questionnaire.questionnaireElementsOmitteds[elementIndex].observations = observations;
        } else {
            state.questionnaire.questionnaireElementsOmitteds.push({ element, observations, omitted: false });
        }
    },
    removeElementsObservation(state, element) {
        if (!state.questionnaire.questionnaireElementsOmitteds) {            
            return;
        }
        const elementIndex = state.questionnaire.questionnaireElementsOmitteds.findIndex(e => e.element.id === element.id);
        if (elementIndex !== -1) {
            if (state.questionnaire.questionnaireElementsOmitteds[elementIndex].omitted) {
                state.questionnaire.questionnaireElementsOmitteds[elementIndex].observations = null;   
            } else {
                state.questionnaire.questionnaireElementsOmitteds.splice(elementIndex, 1);
            }            
        }
    },
    fillOmittedElements(state, omittedElements) {
        state.questionnaire.questionnaireElementsOmitteds = omittedElements;
    },
    resetQuestionnaire(state) {
        state.questionnaire = {};
    },
    initializeQuestionnaire(state) {
        state.questionnaire = {
            questionnaireAnswers: [],
            questionnaireElementsOmitteds: []
        };
    }
};


export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations
};